import { Injectable } from '@angular/core';

import { parse } from '@loaders.gl/core';
import { LASLoader } from '@loaders.gl/las';

import * as THREE from 'three';

interface LasAttribute<T extends ArrayBufferView> {
  value: T;
  size?: number;
}

interface ParsedLasData {
  attributes: {
    [attributeName: string]:
      | LasAttribute<ArrayBufferView>
      | undefined;
  };
}

export interface VolumeLasLoaderOptions {
  pointSize: number;
  pointColor: number;

  classificationColor1: number;
  classificationColor2: number;
}

export interface LoadedLasPointCloud {
  pointCloud: THREE.Points<
    THREE.BufferGeometry,
    THREE.PointsMaterial
  >;

  pointCount: number;
  origin: THREE.Vector3;
  classifications: Set<number>;
}

@Injectable({
  providedIn: 'root'
})
export class VolumeLasLoaderService {

  async load(
    buffer: ArrayBuffer,
    options: VolumeLasLoaderOptions
  ): Promise<LoadedLasPointCloud> {

    const parsed = await parse(
      buffer,
      LASLoader,
      {
        las: {
          shape: 'mesh',
          fp64: false,
          skip: 1,
          colorDepth: 'auto'
        }
      }
    ) as unknown as ParsedLasData;

    const sourcePositions =
      parsed.attributes['POSITION']?.value as
        | Float32Array
        | Float64Array
        | undefined;

    if (!sourcePositions) {
      throw new Error(
        'Le fichier LAS/LAZ ne contient aucun attribut POSITION.'
      );
    }

    const pointCount =
      Math.floor(sourcePositions.length / 3);

    if (pointCount === 0) {
      throw new Error(
        'Le fichier LAS/LAZ ne contient aucun point.'
      );
    }

    const sourceClassifications =
      (
        parsed.attributes['classification']?.value ??
        parsed.attributes['CLASSIFICATION']?.value ??
        parsed.attributes['Classification']?.value
      ) as Uint8Array | undefined;

    const origin = new THREE.Vector3(
      sourcePositions[0],
      sourcePositions[1],
      sourcePositions[2]
    );

    const positions =
      new Float32Array(pointCount * 3);

    const colors =
      new Float32Array(pointCount * 3);

    const classifications =
      new Uint8Array(pointCount);

    const availableClassifications =
      new Set<number>();

    /*
     * Première passe :
     * récupération des classifications disponibles.
     */
    if (sourceClassifications) {
      for (
        let index = 0;
        index < pointCount;
        index++
      ) {
        availableClassifications.add(
          sourceClassifications[index] ?? 0
        );
      }
    }

    const sortedClassifications = [
      ...availableClassifications
    ].sort((left, right) => left - right);

    if (sortedClassifications.length > 2) {
      throw new Error(
        `Le fichier contient ${sortedClassifications.length} classifications au lieu de 2 : ` +
        sortedClassifications.join(', ')
      );
    }

    const firstClassification =
      sortedClassifications[0];

    const secondClassification =
      sortedClassifications[1];

    const firstColor =
      new THREE.Color(
        options.classificationColor1
      );

    const secondColor =
      new THREE.Color(
        options.classificationColor2
      );

    const fallbackColor =
      new THREE.Color(
        options.pointColor
      );

    /*
     * Deuxième passe :
     * construction des positions et des couleurs.
     */
    for (
      let index = 0;
      index < pointCount;
      index++
    ) {
      const offset = index * 3;

      positions[offset] =
        sourcePositions[offset] - origin.x;

      positions[offset + 1] =
        sourcePositions[offset + 1] - origin.y;

      positions[offset + 2] =
        sourcePositions[offset + 2] - origin.z;

      const classification =
        sourceClassifications?.[index] ?? 0;

      classifications[index] =
        classification;

      let color = fallbackColor;

      if (
        firstClassification !== undefined &&
        classification === firstClassification
      ) {
        color = firstColor;
      } else if (
        secondClassification !== undefined &&
        classification === secondClassification
      ) {
        color = secondColor;
      }

      colors[offset] = color.r;
      colors[offset + 1] = color.g;
      colors[offset + 2] = color.b;
    }

    const geometry =
      new THREE.BufferGeometry();

    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(
        positions,
        3
      )
    );

    geometry.setAttribute(
      'color',
      new THREE.BufferAttribute(
        colors,
        3
      )
    );

    geometry.setAttribute(
      'classification',
      new THREE.Uint8BufferAttribute(
        classifications,
        1
      )
    );

    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    const material =
      new THREE.PointsMaterial({
        size: options.pointSize,
        sizeAttenuation: true,
        vertexColors: true
      });

    const pointCloud =
      new THREE.Points<
        THREE.BufferGeometry,
        THREE.PointsMaterial
      >(
        geometry,
        material
      );

    pointCloud.name =
      'volume-point-cloud';

    /*
     * On mémorise les deux classifications utilisées
     * afin de pouvoir recolorer sans reparser le LAZ.
     */
    pointCloud.userData['classification1'] =
      firstClassification;

    pointCloud.userData['classification2'] =
      secondClassification;

    return {
      pointCloud,
      pointCount,
      origin,
      classifications:
      availableClassifications
    };
  }

  updateClassificationColors(
    pointCloud: THREE.Points,
    classificationColor1: number,
    classificationColor2: number,
    fallbackColor: number
  ): void {

    const geometry =
      pointCloud.geometry;

    if (!(geometry instanceof THREE.BufferGeometry)) {
      return;
    }

    const classificationAttribute =
      geometry.getAttribute('classification');

    const colorAttribute =
      geometry.getAttribute('color');

    if (
      !classificationAttribute ||
      !colorAttribute
    ) {
      return;
    }

    const classification1 =
      pointCloud.userData['classification1'] as
        | number
        | undefined;

    const classification2 =
      pointCloud.userData['classification2'] as
        | number
        | undefined;

    const firstColor =
      new THREE.Color(
        classificationColor1
      );

    const secondColor =
      new THREE.Color(
        classificationColor2
      );

    const defaultColor =
      new THREE.Color(
        fallbackColor
      );

    const pointCount =
      classificationAttribute.count;

    for (
      let index = 0;
      index < pointCount;
      index++
    ) {
      const classification =
        classificationAttribute.getX(index);

      let color = defaultColor;

      if (
        classification1 !== undefined &&
        classification === classification1
      ) {
        color = firstColor;
      } else if (
        classification2 !== undefined &&
        classification === classification2
      ) {
        color = secondColor;
      }

      colorAttribute.setXYZ(
        index,
        color.r,
        color.g,
        color.b
      );
    }

    colorAttribute.needsUpdate = true;
  }
}
